import { useLiveQuery } from 'dexie-react-hooks'
import { Building2, ChevronRight, Mail, MapPin, Phone, Users } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { resumesClients } from '../lib/clients'
import { nouvelId } from '../lib/id'
import { formatEuro } from '../lib/money'
import { rechercher } from '../lib/recherche'
import { getBase } from '../outbox/base'
import { creer, lister } from '../outbox/depot'
import { Avatar } from '../ui/composants/avatar'
import { Bouton } from '../ui/composants/bouton'
import { ChampRecherche } from '../ui/composants/champ-recherche'
import { EnteteSection } from '../ui/composants/entete-section'
import { Feuille } from '../ui/composants/feuille'
import { useImpayes } from '../ui/impayes'
import { BarreActions, CHAMP } from '../ui/synchro'

/** Liste des clients : recherche par nom, téléphone, ville ou e-mail, devis et reste à encaisser de chacun, création. */
export default function Clients() {
  const base = getBase()
  const naviguer = useNavigate()
  const { pathname } = useLocation()
  const impayes = useImpayes()
  const donnees = useLiveQuery(async () => ({ clients: await lister(base, 'clients'), devis: await lister(base, 'devis') }), [])
  const [saisie, setSaisie] = useState('')
  const [creation, setCreation] = useState(false)
  const [nom, setNom] = useState('')
  const [telephone, setTelephone] = useState('')
  if (!donnees) return <main className="flex-1" />
  const resumes = resumesClients(donnees.clients, donnees.devis, impayes ?? [])
  const trouves = rechercher(resumes, saisie, (r) => [r.client.nom, r.client.telephone, r.client.email, r.client.ville, r.client.codePostal, r.client.siret])

  async function creerClient(e: FormEvent) {
    e.preventDefault()
    if (!nom.trim()) return
    const c = await creer(base, 'clients', { id: nouvelId(), nom: nom.trim(), ...(telephone.trim() && { telephone: telephone.trim() }) })
    naviguer(`/clients/${c.id}`)
  }

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <EnteteSection Icone={Users} titre={`Clients (${donnees.clients.length})`} description="Rechercher, consulter et gérer vos clients." />
        <ChampRecherche libelle="Rechercher (nom, téléphone, ville, e-mail, SIRET)" placeholder="Nom, téléphone, ville, e-mail, SIRET…" value={saisie} onChange={setSaisie} />
        {donnees.clients.length === 0 && <p className="text-slate-700">Aucun client : « + Nouveau client » pour commencer, ou importez un fichier Excel (Import / export).</p>}
        {donnees.clients.length > 0 && trouves.length === 0 && <p className="text-slate-700">Aucun client ne correspond à « {saisie} ».</p>}
        <ul className="flex flex-col gap-3" aria-label="Clients">
          {trouves.map((r) => {
            const c = r.client
            const professionnel = Boolean(c.siret)
            const lieu = [c.ville, c.codePostal && `(${c.codePostal.slice(0, 2)})`].filter(Boolean).join(' ')
            const coordonnees = [
              { Icone: Phone, texte: c.telephone },
              { Icone: MapPin, texte: lieu || undefined },
              { Icone: Mail, texte: c.email },
              { Icone: Building2, texte: c.siret && `SIRET : ${c.siret}` },
            ].filter((l) => l.texte)
            return (
              <li key={c.id}>
                <Link to={`/clients/${c.id}`} aria-current={pathname === `/clients/${c.id}` ? 'page' : undefined} className="flex min-h-14 items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm active:bg-slate-50 aria-[current=page]:border-nuit aria-[current=page]:bg-blue-50">
                  <Avatar nom={c.nom} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-lg leading-tight font-bold">{c.nom}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${professionnel ? 'bg-blue-100 text-blue-900' : 'bg-slate-200 text-slate-900'}`}>
                        {professionnel ? 'Professionnel' : 'Particulier'}
                      </span>
                    </span>
                    <span className="block text-sm text-slate-700">
                      {r.devis} devis{r.signes > 0 && ` dont ${r.signes} signé${r.signes > 1 ? 's' : ''}`}
                    </span>
                    {coordonnees.length === 0 ? (
                      <span className="mt-1 block text-sm text-slate-700">Fiche à compléter</span>
                    ) : (
                      <span className="mt-2 flex flex-col gap-1">
                        {coordonnees.map(({ Icone, texte }) => (
                          <span key={texte} className="flex items-center gap-2 text-sm">
                            <Icone aria-hidden className="size-4 shrink-0 text-slate-700" />
                            <span className="truncate">{texte}</span>
                          </span>
                        ))}
                      </span>
                    )}
                    {r.impayeTtc > 0 && <span className="mt-1 block text-sm font-semibold text-red-800">{formatEuro(r.impayeTtc)} dus</span>}
                  </span>
                  <ChevronRight aria-hidden className="mt-3 size-5 shrink-0 text-slate-500" />
                </Link>
              </li>
            )
          })}
        </ul>
      </main>

      <Feuille ouverte={creation} onChange={setCreation} titre="Nouveau client">
        <form className="flex flex-col gap-3" onSubmit={creerClient}>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-slate-700">Nom du client</span>
            <input className={CHAMP} value={nom} onChange={(e) => setNom(e.target.value)} autoComplete="off" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-slate-700">Téléphone (facultatif)</span>
            <input className={CHAMP} type="tel" inputMode="tel" value={telephone} onChange={(e) => setTelephone(e.target.value)} autoComplete="off" />
          </label>
          <Bouton type="submit" disabled={!nom.trim()}>
            Créer et compléter la fiche
          </Bouton>
        </form>
      </Feuille>

      <BarreActions>
        <Bouton className="flex-1" onClick={() => setCreation(true)}>
          + Nouveau client
        </Bouton>
      </BarreActions>
    </>
  )
}
